import { useLocation } from "react-router-dom";
import { en } from "./en";
import { ro } from "./ro";
import { ru } from "./ru";
import type { Dict } from "./types";
import { CASE_SLUGS } from "@/site/caseSlugs";
import { SERVICE_SLUGS } from "@/site/serviceSlugs";
import { BLOG_SLUGS } from "@/site/blog";
import { NICHE_SLUGS } from "@/site/niches";

/** Trei limbi, engleza fără prefix.
 *
 *  De ce engleza stă la rădăcină și nu la /en: adresele existente sunt indexate
 *  și date clienților. Mutarea lor sub /en ar cere redirecturi pentru fiecare,
 *  ar rupe orice link partajat până se propagă, și n-ar aduce nimic — un prefix
 *  pentru limba implicită e cerut doar când niciuna nu e implicită.
 *
 *  Paginile legale rămân doar în engleză, deliberat: Privacy și Terms sunt
 *  documente care obligă, iar două traduceri care deviază într-o formulare te
 *  obligă pe amândouă. Versiunea engleză e cea care guvernează. */
export const LANGS = ["en", "ro", "ru"] as const;
export type Lang = (typeof LANGS)[number];

export const DICTS: Record<Lang, Dict> = { en, ro, ru };

/** Eticheta din comutator și numele întreg, în limba respectivă — un vorbitor de
 *  rusă caută „Русский", nu „Russian". */
export const LANG_LABEL: Record<Lang, string> = { en: "EN", ro: "RO", ru: "RU" };
export const LANG_NAME: Record<Lang, string> = { en: "English", ro: "Română", ru: "Русский" };

/** Codul pentru atributul lang și pentru hreflang. */
export const LANG_TAG: Record<Lang, string> = { en: "en", ro: "ro", ru: "ru" };

/** Paginile care există în toate trei limbile. Legalele lipsesc intenționat.
 *
 *  Derivată din listele de slug-uri, nu scrisă de mână. Scrisă de mână a fost,
 *  și a rămas în urmă de trei ori: la cele trei cazuri de site, la paginile de
 *  serviciu și la „Despre". Consecința nu era vizibilă în build — rutele
 *  existau, erau prerandate și stăteau în sitemap — dar `useLangPath` nu le
 *  găsea în listă, deci lăsa adresa fără prefix: un vizitator care citea în
 *  română apăsa pe un serviciu și ajungea în engleză. Iar `hasTranslation`
 *  hotărăște și dacă pagina primește alternative hreflang, și unde duce
 *  comutatorul de limbă — care, negăsind pagina, arunca omul pe prima pagină.
 *
 *  O rută nouă intră acum singură, din aceeași listă din care o ia și routerul. */
export const TRANSLATED_PATHS = [
  "/",
  "/work",
  "/services",
  "/faq",
  "/contact",
  "/about",
  ...CASE_SLUGS.map((s) => `/work/${s}`),
  ...SERVICE_SLUGS.map((s) => `/services/${s}`),
  "/tools/crawlview",
  "/blog",
  ...BLOG_SLUGS.map((s) => `/blog/${s}`),
  /* A patra oară. Nișele există în toate trei limbile de la prima zi, dar nu
     erau aici: pe /ro/stomatologie comutatorul de limbă arunca vizitatorul pe
     prima pagină în loc să-l ducă la /ru/stomatologie. Doar comutatorul —
     hreflang-ul îl scrie serverul din regula de prefix, deci alternativele
     erau corecte în HTML tot timpul. Un om care schimba limba pe o pagină de
     nișă își pierdea pagina; un crawler nu observa nimic. */
  "/industries",
  ...NICHE_SLUGS.map((s) => `/${s}`),
] as const;

const PREFIXES: Record<string, Lang> = { ro: "ro", ru: "ru" };

/** Limba din adresă. Orice altceva înseamnă engleză, inclusiv 404-urile. */
export function langFromPath(pathname: string): Lang {
  const seg = pathname.split("/")[1] ?? "";
  return PREFIXES[seg] ?? "en";
}

/** Adresa fără prefixul de limbă: „/ro/work/erp" → „/work/erp". */
export function stripLang(pathname: string): string {
  const lang = langFromPath(pathname);
  if (lang === "en") return pathname || "/";
  const rest = pathname.slice(lang.length + 1);
  return rest === "" ? "/" : rest;
}

/** Adresa în limba cerută. „/" în engleză rămâne „/", nu „/en". */
export function withLang(lang: Lang, path: string): string {
  const base = path.startsWith("/") ? path : `/${path}`;
  if (lang === "en") return base;
  return base === "/" ? `/${lang}` : `/${lang}${base}`;
}

/** Există pagina asta în limba cerută? Legalele nu, deci linkul spre ele nu
 *  primește prefix și rămâne pe engleză. */
export function hasTranslation(path: string): boolean {
  return (TRANSLATED_PATHS as readonly string[]).includes(path);
}

export function useLang(): Lang {
  return langFromPath(useLocation().pathname);
}

export function useT(): Dict {
  return DICTS[useLang()];
}

/** Constructor de adrese care păstrează limba curentă. Folosit în locul lui
 *  `to="/work/erp"`, altfel fiecare link ar scoate vizitatorul din limba lui. */
export function useLangPath(): (path: string) => string {
  const lang = useLang();
  return (path: string) => (hasTranslation(path) ? withLang(lang, path) : path);
}
